import { Vector2, Vector3 } from "three";
import { RAYLEIGH_DEPTH, MIE_DEPTH, OZONE_DEPTH } from "./earth-lighting";

// What a camera does with the Sun in view, as an HDR pass before tone
// mapping (so it's exposed with the world): glare around the disc (the lens'
// point spread), a starburst from a six-blade aperture, ghost reflections of
// the aperture along the line through the image centre, and a faint
// chromatic halo. It fades with how much of the Sun the Earth hides and
// takes the Sun's colour through the air, so it reddens and dims as the Sun
// sets behind the limb.

const EARTH_RADIUS_KM = 6371;
const RAYLEIGH_SCALE_KM = 8;
const MIE_SCALE_KM = 1.2;
const ATMOSPHERE_TOP_KM = 100;
// Grazing paths cross the ozone layer (10-50 km) over ~36x its vertical depth.
const OZONE_MAX_AIRMASS = 36;

// Flare brightness on screen, independent of the eye-adaptation exposure
// (divided out), so glare doesn't swell when looking at the night side.
const FLARE_STRENGTH = 1.2;

const vertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
uniform sampler2D tDiffuse;
uniform vec2 uSun;        // Sun on screen, in units of screen height, centred
uniform float uAspect;
uniform vec3 uColour;     // Sun colour through the air x visible fraction x gain

varying vec2 vUv;

float hash(float n) { return fract(sin(n) * 43758.5453); }
float noise(float x) {
  float i = floor(x);
  float f = fract(x);
  return mix(hash(i), hash(i + 1.0), f * f * (3.0 - 2.0 * f));
}

// Soft aperture ghost with a slightly brighter rim.
float ghost(vec2 p, vec2 c, float r) {
  float d = length(p - c);
  return smoothstep(r, r * 0.8, d) * (0.55 + 0.45 * smoothstep(r * 0.3, r, d));
}

float ring(float r, float radius, float width) {
  return exp(-pow((r - radius) / width, 2.0));
}

void main() {
  vec3 scene = texture2D(tDiffuse, vUv).rgb;
  vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
  vec2 d = p - uSun;
  float r = length(d);
  float angle = atan(d.y, d.x);

  // Glare: a bright core, a wider veil and a faint long tail.
  float glare = 0.6 / (1.0 + pow(r / 0.008, 2.0))
              + 0.05 / (1.0 + pow(r / 0.06, 2.0))
              + 0.01 / (1.0 + pow(r / 0.35, 2.0));

  // Starburst: six diffraction spikes plus fine irregular rays.
  float spikes = pow(abs(cos(3.0 * angle + 0.35)), 400.0);
  float rays = pow(noise(angle * 30.0 + 11.0) * noise(angle * 71.0 + 3.0), 2.0);
  float burst = (spikes * 1.2 + rays * 0.8) * 0.004 / (r + 0.004) * smoothstep(0.7, 0.0, r);

  // Halo: a faint ring round the Sun, red outermost.
  vec3 halo = vec3(ring(r, 0.305, 0.012), ring(r, 0.295, 0.012), ring(r, 0.285, 0.012)) * 0.006;

  // Ghosts: reflections at points along the axis through the image centre.
  vec3 ghosts = vec3(0.0);
  ghosts += ghost(p, uSun * -0.35, 0.035) * vec3(0.20, 0.35, 0.25);
  ghosts += ghost(p, uSun * -0.60, 0.018) * vec3(0.35, 0.25, 0.15);
  ghosts += ghost(p, uSun * -1.00, 0.070) * vec3(0.08, 0.12, 0.22);
  ghosts += ghost(p, uSun * -1.45, 0.110) * vec3(0.10, 0.06, 0.04);
  ghosts += ghost(p, uSun * 0.45, 0.025) * vec3(0.12, 0.20, 0.30);
  ghosts *= 0.05;

  vec3 flare = uColour * (vec3(glare + burst) + halo + ghosts);
  gl_FragColor = vec4(scene + flare, 1.0);
}
`;

export const LensFlareShader = {
  uniforms: {
    tDiffuse: { value: null },
    uSun: { value: new Vector2() },
    uAspect: { value: 1 },
    uColour: { value: new Vector3() },
  },
  vertexShader,
  fragmentShader,
};

// Fraction of a disc of angular radius rs whose centre is `gap` outside the
// edge of a much larger disc (negative = inside): a circular segment.
export function visibleFraction(gap, rs) {
  const x = Math.max(-1, Math.min(1, gap / rs));
  return (Math.acos(-x) + x * Math.sqrt(1 - x * x)) / Math.PI;
}

// Transmittance [r, g, b] of the air along the ray from `from` towards `to`,
// with the planet (radius) at the origin, from the ray's closest approach:
// optical depth = vertical depth x sqrt(2 pi R / H) x exp(-h / H).
export function transmittanceAlong(from, to, radius) {
  const dir = new Vector3().subVectors(to, from).normalize();
  const along = -from.dot(dir);
  if (along <= 0) return [1, 1, 1]; // moving away from the planet
  const closest = from.clone().addScaledVector(dir, along).length();
  const h = ((closest - radius) / radius) * EARTH_RADIUS_KM;
  if (h >= ATMOSPHERE_TOP_KM) return [1, 1, 1];
  if (h <= 0) return [0, 0, 0];
  const airmassR = Math.sqrt((2 * Math.PI * EARTH_RADIUS_KM) / RAYLEIGH_SCALE_KM) * Math.exp(-h / RAYLEIGH_SCALE_KM);
  const airmassM = Math.sqrt((2 * Math.PI * EARTH_RADIUS_KM) / MIE_SCALE_KM) * Math.exp(-h / MIE_SCALE_KM);
  const airmassO = Math.min(airmassR, OZONE_MAX_AIRMASS);
  return [0, 1, 2].map((i) => Math.exp(-(RAYLEIGH_DEPTH[i] * airmassR + MIE_DEPTH * airmassM + OZONE_DEPTH[i] * airmassO)));
}

// Per-frame state of the Sun as seen by `camera`: colour through the air and
// how much of it the planet hides. Shared by the disc and the flare.
// `moon`: optional { position, radius } of another body that can cover the Sun.
export function sunView(camera, sunPosition, sunRadius, planetRadius, moon = null) {
  const cam = camera.position;
  const toSun = new Vector3().subVectors(sunPosition, cam);
  const sunAngle = Math.atan(sunRadius / toSun.length());
  const earthAngle = Math.asin(Math.min(1, planetRadius / cam.length()));
  const separation = toSun.clone().normalize().angleTo(cam.clone().negate().normalize());
  let visible = visibleFraction(separation - earthAngle, sunAngle);
  if (moon) {
    const toMoon = new Vector3().subVectors(moon.position, cam);
    if (toMoon.length() < toSun.length()) {
      const moonAngle = Math.asin(Math.min(1, moon.radius / toMoon.length()));
      visible *= visibleFraction(toSun.angleTo(toMoon) - moonAngle, sunAngle);
    }
  }
  return { transmittance: transmittanceAlong(cam, sunPosition, planetRadius), visible };
}

export function updateLensFlare(pass, camera, sunPosition, view, exposure, aspect) {
  const ndc = sunPosition.clone().project(camera);
  const inFront = ndc.z < 1;
  // Fade out as the Sun leaves the frame (ghosts and glare need it in view).
  const edge = Math.max(Math.abs(ndc.x), Math.abs(ndc.y));
  const onScreen = inFront ? Math.max(0, Math.min(1, (1.25 - edge) / 0.25)) : 0;
  const strength = view.visible * onScreen * (FLARE_STRENGTH / exposure);
  pass.enabled = strength * Math.max(...view.transmittance) > 1e-4;
  if (!pass.enabled) return;
  const u = pass.uniforms;
  u.uSun.value.set((ndc.x * 0.5) * aspect, ndc.y * 0.5);
  u.uAspect.value = aspect;
  u.uColour.value.set(...view.transmittance).multiplyScalar(strength);
}
